import { useMemo } from "react";
import { useTeamStats } from "../../hooks/useTeamStats";
import type { TeamNurse } from "../../utils/models";
import { getNeverLoggedInNurseIds } from "./useGetTeamStats";

export type TeamScheduleStats = {
	workedLastMonthNurseIds: Set<number>;
	recurringShiftsNurseIds: Set<number>;
	/** Null when the department has no self-scheduling period: the card is hidden. */
	selfSchedulingSubmittedNurseIds: Set<number> | null;
};

export type TeamCardStats = {
	totalStaff: number;
	neverLoggedInNurseIds: Set<number>;
	/** Undefined until the server answers, and after it fails. */
	schedule: TeamScheduleStats | undefined;
	isSchedulePending: boolean;
	isScheduleError: boolean;
};

/**
 * The figures behind the Team page's stat cards. Roster-based ones are counted here; the schedule-based
 * ones come from the server, narrowed to the roster so a card never counts someone the table can't show.
 */
export const useTeamCardStats = (departmentId: string, roster: TeamNurse[]): TeamCardStats => {
	const { stats, isLoading, isError } = useTeamStats(departmentId);

	const schedule = useMemo<TeamScheduleStats | undefined>(() => {
		// A failed refetch keeps the last answer in `stats`; dropping it keeps a hidden tile from still filtering rows.
		if (!stats || isError) return undefined;
		const rosterIds = new Set(roster.map((n) => n.id));
		const onRoster = (ids: number[]) => new Set(ids.filter((id) => rosterIds.has(id)));
		return {
			workedLastMonthNurseIds: onRoster(stats.workedLastMonthNurseIds),
			recurringShiftsNurseIds: onRoster(stats.recurringShiftsNurseIds),
			selfSchedulingSubmittedNurseIds: stats.selfSchedulingSubmittedNurseIds === null ? null : onRoster(stats.selfSchedulingSubmittedNurseIds),
		};
	}, [stats, isError, roster]);

	const neverLoggedInNurseIds = useMemo(() => getNeverLoggedInNurseIds(roster), [roster]);

	return { totalStaff: roster.length, neverLoggedInNurseIds, schedule, isSchedulePending: isLoading, isScheduleError: isError };
};
